import API from '@Services/apiAxios';
import axios from 'axios';

export interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export interface AccountAiFields {
  email?: string;
  name?: string;
  companyName?: string;
  contactNumber?: string;
  address?: string;
  [key: string]: any;
}

export interface AccountExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  fields?: AccountAiFields;
  confidence?: number;
  invalidFields?: string[];
  error?: string | null;
  [key: string]: any;
}

/**
 * Step 1: Get presigned upload URL for extraction
 * POST /speedboxbot/extract/upload-url
 */
export const getUploadUrl = async (file: File): Promise<PresignedUrlResponse> => {
  const { data } = await API.post<PresignedUrlResponse>('/speedboxbot/extract/upload-url', {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
  });
  return data;
};

/**
 * Step 2: Upload file directly to S3 bucket via presigned POST
 */
export const uploadToS3 = async (
  uploadUrlResponse: PresignedUrlResponse,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  const { url, fields } = uploadUrlResponse;
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  // Use clean axios instance for S3 request to prevent sending custom auth headers to AWS
  await axios.post(url, formData, {
    onUploadProgress: progressEvent => {
      if (onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        onProgress(percent);
      }
    },
  });
};

/**
 * Step 3: Trigger document extraction with documentType = "account_creation"
 * POST /speedboxbot/extract
 */
export const extractAccountDocument = async (s3Key: string): Promise<AccountExtractionResponse> => {
  const { data } = await API.post<AccountExtractionResponse>('/speedboxbot/extract', {
    documentType: 'account_creation',
    s3Key,
  });
  return data;
};

/**
 * Full 3-step AI extraction runner
 */
export const processAccountDocumentExtraction = async (
  file: File,
  onStatusChange?: (status: string) => void,
  onUploadProgress?: (percent: number) => void,
): Promise<AccountExtractionResponse> => {
  onStatusChange?.('Generating secure upload URL...');
  const presigned = await getUploadUrl(file);

  onStatusChange?.('Uploading document securely to S3...');
  const s3Key = presigned.key || presigned.fields?.['key'];
  await uploadToS3(presigned, file, onUploadProgress);

  onStatusChange?.('Extracting account details using AI...');
  const response = await extractAccountDocument(s3Key);

  return response;
};
